import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import TeacherDashboard from './TeacherDashboard';
import StudentDashboard from './StudentDashboard';

export default function Dashboard() {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) throw error;

      if (user) {
        const { data: teacherData, error: teacherError } = await supabase
          .from('teachers')
          .select('*')
          .eq('id', user.id)
          .single();

        if (teacherData) {
          setUserDetails({ ...teacherData, role: 'teacher' });
        } else {
          const { data: studentData, error: studentError } = await supabase
            .from('students')
            .select('*')
            .eq('id', user.id)
            .single();

          if (studentData) {
            setUserDetails({ ...studentData, role: 'student' });
          } else {
            throw new Error('No user details found');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      alert('Error logging out: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <svg className="w-20 h-20 mx-auto mb-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle className="text-gray-600" stroke="currentColor" strokeWidth="4" fill="none" cx="50" cy="50" r="45" />
            <path className="text-blue-500" fill="currentColor" d="M50,25 L75,50 L50,75 L25,50 Z">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 50 50"
                to="360 50 50"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
          <h2 className="text-2xl font-semibold text-white mb-2">LMS</h2>
          <p className="text-gray-400">Loading your personalized learning experience...</p>
        </div>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          svg circle {
            animation: dash 1.5s ease-in-out infinite;
          }
          @keyframes dash {
            0% {
              stroke-dasharray: 1, 150;
              stroke-dashoffset: 0;
            }
            50% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -35;
            }
            100% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -124;
            }
          }
        `}</style>
      </div>
    );
  }

  if (!userDetails) {
    return <div className="flex justify-center items-center h-screen">No user details found.</div>;
  }

  return (
    <>
      {userDetails.role === 'teacher' ? (
        <TeacherDashboard teacherDetails={userDetails} handleLogout={handleLogout} />
      ) : (
        <StudentDashboard studentDetails={userDetails} handleLogout={handleLogout} />
      )}
    </>
  );
}